import { apiClient } from '../api/apiClient';

export interface LeaveSlot {
  id: number;
  doctor_id: number;
  practice_id?: number | null;
  leave_date: string; // YYYY-MM-DD
  start_time: string; // HH:mm
  end_time: string; // HH:mm
  leave_type: string;
  reason?: string | null;
  leave_scope: string;
  status: string; // active, cancelled, expired
  has_existing_appointments: boolean;
  affected_appointments?: AffectedAppointment[];
  created_at: string;
  updated_at?: string | null;
  cancelled_at?: string | null;
}

export interface AffectedAppointment {
  id: number;
  patient_id?: number | null;
  patient_name?: string; // Optional - backend doesn't always include this
  appointment_date: string; // ISO datetime string
  status: string;
}

export interface LeaveSlotCreate {
  practice_id?: number | null;
  leave_date: string; // YYYY-MM-DD
  start_time: string; // HH:mm
  end_time: string; // HH:mm
  leave_type: string;
  reason?: string | null;
  leave_scope: string;
  auto_cancel_appointments: boolean;
}

export interface LeaveSlotUpdate {
  leave_date?: string; // YYYY-MM-DD
  start_time?: string; // HH:mm
  end_time?: string; // HH:mm
  leave_type?: string;
  reason?: string | null;
  leave_scope?: string;
}

export interface LeaveSlotBulkCreate {
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  start_time: string; // HH:mm
  end_time: string; // HH:mm
  leave_type: string;
  reason?: string | null;
  leave_scope: string;
  practice_id?: number | null;
  auto_cancel_appointments: boolean;
}

export interface AppointmentAction {
  appointment_id: number;
  action: 'cancel' | 'reschedule' | 'keep';
  new_appointment_date?: string; // ISO datetime string
}

export interface SelectiveAppointmentHandling {
  appointment_actions: AppointmentAction[];
}

export interface AppointmentCount {
  date: string; // YYYY-MM-DD
  count: number;
}

class LeaveSlotService {
  /**
   * Get leave slots for the current doctor
   */
  async getLeaveSlots(params?: {
    start_date?: string;
    end_date?: string;
    practice_id?: number;
    status?: string;
  }): Promise<LeaveSlot[]> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.practice_id) queryParams.append('practice_id', params.practice_id.toString());
    if (params?.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const path = `/api/v1/doctors/leave-slots${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<LeaveSlot[]>(path);
    return response.data;
  }

  /**
   * Get a specific leave slot by ID
   */
  async getLeaveSlot(leaveSlotId: number): Promise<LeaveSlot> {
    const response = await apiClient.get<LeaveSlot>(`/api/v1/doctors/leave-slots/${leaveSlotId}`);
    return response.data;
  }

  /**
   * Create a leave slot for a practice
   */
  async createLeaveSlot(practiceId: number, leaveSlot: LeaveSlotCreate): Promise<LeaveSlot> {
    const response = await apiClient.post<LeaveSlot>(
      `/api/v1/doctors/practices/${practiceId}/leave-slots`,
      leaveSlot
    );
    return response.data;
  }

  /**
   * Create leave slot with selective appointment handling
   */
  async createLeaveWithSelectiveHandling(
    practiceId: number,
    leaveSlot: LeaveSlotCreate,
    appointmentHandling: SelectiveAppointmentHandling
  ): Promise<LeaveSlot> {
    const response = await apiClient.post<LeaveSlot>(
      `/api/v1/doctors/practices/${practiceId}/leave-slots/selective-handling`,
      {
        ...leaveSlot,
        ...appointmentHandling,
      }
    );
    return response.data;
  }

  /**
   * Bulk create leave slots (multiple days)
   */
  async bulkCreateLeaveSlots(practiceId: number, bulkCreate: LeaveSlotBulkCreate): Promise<LeaveSlot[]> {
    const response = await apiClient.post<LeaveSlot[]>(
      `/api/v1/doctors/practices/${practiceId}/leave-slots/bulk`,
      bulkCreate
    );
    return response.data;
  }

  /**
   * Update a leave slot
   */
  async updateLeaveSlot(
    leaveSlotId: number,
    update: LeaveSlotUpdate,
    autoCancelAppointments: boolean = false
  ): Promise<LeaveSlot> {
    const queryParams = new URLSearchParams();
    if (autoCancelAppointments) {
      queryParams.append('auto_cancel_appointments', 'true');
    }
    const queryString = queryParams.toString();
    const path = `/api/v1/doctors/leave-slots/${leaveSlotId}${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.put<LeaveSlot>(path, update);
    return response.data;
  }

  /**
   * Cancel a leave slot
   */
  async cancelLeaveSlot(leaveSlotId: number, restoreAppointments: boolean = false): Promise<void> {
    const queryParams = new URLSearchParams();
    if (restoreAppointments) {
      queryParams.append('restore_appointments', 'true');
    }
    const queryString = queryParams.toString();
    const path = `/api/v1/doctors/leave-slots/${leaveSlotId}${queryString ? `?${queryString}` : ''}`;
    await apiClient.delete(path);
  }

  /**
   * Check for conflicts before creating a leave slot
   * Note: This endpoint doesn't exist in the backend. Conflicts are returned by the create endpoint.
   * This method is kept for compatibility but should not be used.
   */
  async checkConflicts(params: {
    practice_id?: number | null;
    leave_date: string;
    start_time: string;
    end_time: string;
    leave_scope: string;
  }): Promise<{ has_conflicts: boolean; conflicting_appointments: AffectedAppointment[] }> {
    // This endpoint doesn't exist - conflicts are checked during creation
    // Return empty result to avoid errors
    return {
      has_conflicts: false,
      conflicting_appointments: [],
    };
  }

  /**
   * Get appointment counts per date for a date range
   */
  async getAppointmentCounts(startDate: string, endDate: string): Promise<Record<string, number>> {
    const response = await apiClient.get<{ counts: AppointmentCount[] }>(
      `/api/v1/doctors/appointments/counts?start_date=${startDate}&end_date=${endDate}`
    );
    
    // Convert array to object mapping date -> count
    const countsMap: Record<string, number> = {};
    if (response.data.counts && Array.isArray(response.data.counts)) {
      response.data.counts.forEach((item) => {
        countsMap[item.date] = item.count;
      });
    }
    return countsMap;
  }
}

export const leaveSlotService = new LeaveSlotService();

